import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

export function getCielaRingPicker(element) {
  const scope = element.closest('.shopify-section, dialog, product-card');
  const productId =
    element.dataset.productId ||
    element.closest('product-form-component')?.dataset.productId;

  return Array.from(
    scope?.querySelectorAll('variant-picker[data-ciela-ring-size]') || []
  ).find(
    (picker) =>
      picker.dataset.productId === productId &&
      picker.closest('.shopify-section, dialog, product-card') === scope
  );
}

export default class VariantPicker extends Component {
  #pendingRequestUrl;
  #abortController;
  #checkedIndices = [];
  #radios = [];
  #cielaChosenSize = '';
  #cielaResolvedVariant = '';
  #cielaRequestVersion = 0;

  #cielaChangeHandler = (event) => this.variantChanged(event);

  #cielaClickHandler = (event) => {
    if (
      event.target instanceof Element &&
      event.target.closest('[data-ciela-size-retry]')
    ) {
      const option = this.querySelector(
        '[data-ciela-size-option] input:checked, [data-ciela-size-option] select'
      );

      if (option) this.variantChanged({ target: option });
      else this.cielaPromptSize();
    }
  };

  get cielaResolvedVariantId() {
    return this.#cielaResolvedVariant;
  }

  cielaPromptSize() {
    const field = this.querySelector('[data-ciela-size-option]');
    field?.setAttribute('data-ciela-size-attention', 'true');
    this.querySelector('.ciela-ring-size-select')?.setAttribute('aria-invalid', 'true');
    const control = this.querySelector(
      '[data-ciela-size-option] select, [data-ciela-size-option] input'
    );

    control?.scrollIntoView({
      block: 'center',
      behavior: 'smooth',
    });

    const focusControl = control?.closest('ciela-variant-select')?.querySelector('[data-ciela-select-trigger]') || control;
    focusControl?.focus({ preventScroll: true });
  }

  #cielaSetState(state) {
    if (!this.hasAttribute('data-ciela-ring-size')) return;

    this.dataset.cielaSizeState = state;
    if (state === 'ready' || state === 'pending') {
      this.querySelector('[data-ciela-size-attention]')?.removeAttribute('data-ciela-size-attention');
      this.querySelector('.ciela-ring-size-select')?.removeAttribute('aria-invalid');
    }

    if (
      state === 'ready' && this.dataset.templateProductMatch === 'true' &&
      !this.closest('product-card, quick-add-component, dialog') &&
      /^\d+$/.test(this.dataset.productId || '') &&
      /^\d+$/.test(this.#cielaResolvedVariant)
    ) {
      try {
        sessionStorage.setItem(
          'ciela_confirmed_ring_variant:' + this.dataset.productId,
          this.#cielaResolvedVariant
        );
      } catch (_) {
        // A blocked session store must not prevent a valid size selection.
      }
    }

    const selected = this.querySelector(
      '[data-ciela-size-option] input:checked, [data-ciela-size-option] option[selected][data-option-value-id]'
    );

    const messages = {
      required: 'Please select your ring size before adding to bag.',
      pending: 'Updating your selection…',
      ready: `Selected ring size: ${selected?.value || ''}`,
      unavailable:
        'This combination is unavailable. Please choose another size or option.',
      error: 'We could not update your selection. Please try again.',
    };

    const message = this.querySelector('[data-ciela-size-message]');
    if (message) message.textContent = messages[state] || messages.required;

    const retry = this.querySelector('[data-ciela-size-retry]');
    if (retry) retry.hidden = state !== 'error';

    this.dispatchEvent(
      new CustomEvent('ciela:size-state', { bubbles: true })
    );
  }

  #cielaRestoreUrlSize() {
    if (
      !this.hasAttribute('data-ciela-ring-size') ||
      this.dataset.templateProductMatch !== 'true' ||
      this.closest('product-card, quick-add-component, dialog') ||
      !/^\d+$/.test(this.dataset.productId || '')
    ) return 'required';

    try {
      const variantId = new URL(window.location.href).searchParams.get('variant');
      if (!variantId || !/^\d+$/.test(variantId)) return 'required';
      if (sessionStorage.getItem('ciela_confirmed_ring_variant:' + this.dataset.productId) !== variantId) {
        return 'required';
      }

      const source = this.querySelector('script[type="application/json"]');
      const variant = JSON.parse(source?.textContent || 'null');
      const position = Number(this.dataset.cielaRingSize) - 1;
      if (
        !variant || String(variant.id) !== variantId ||
        !Number.isInteger(position) || position < 0 ||
        !Array.isArray(variant.options) ||
        typeof variant.options[position] !== 'string'
      ) return 'required';

      const size = Array.from(this.querySelectorAll(
        '[data-ciela-size-option] input[type="radio"], [data-ciela-size-option] option[data-option-value-id]'
      )).find(option => option.dataset.optionValueId && option.value === variant.options[position]);
      if (!size) return 'required';

      this.#cielaChosenSize = size.dataset.optionValueId;
      this.#cielaResolvedVariant = variant.available === true ? String(variant.id) : '';
      return variant.available === true ? 'ready' : 'unavailable';
    } catch (_) {
      return 'required';
    }
  }

  #cielaRestoreSize() {
    if (!this.hasAttribute('data-ciela-ring-size')) return;

    const options = this.querySelectorAll(
      '[data-ciela-size-option] input[type="radio"], [data-ciela-size-option] option'
    );

    let found = false;

    for (const option of options) {
      const selected =
        !!this.#cielaChosenSize &&
        option.dataset.optionValueId === this.#cielaChosenSize;

      found ||= selected;

      if (option instanceof HTMLInputElement) {
        option.checked = selected;
        option.toggleAttribute('checked', selected);
        option.dataset.currentChecked = String(selected);
        option.dataset.previousChecked = 'false';
      } else {
        option.selected = selected;
        option.toggleAttribute('selected', selected);
      }
    }

    if (!found) {
      this.#cielaChosenSize = '';

      const placeholder = this.querySelector(
        '[data-ciela-size-option] option[value=""]'
      );

      if (placeholder) {
        placeholder.selected = true;
        placeholder.setAttribute('selected', 'selected');
      }
    }
  }

  #cielaIndexFieldsets() {
    this.#radios = [];
    this.#checkedIndices = [];

    const fieldsets = this.refs.fieldsets || [];

    fieldsets.forEach((fieldset, index) => {
      const radios = Array.from(
        fieldset.querySelectorAll('input[type="radio"]')
      );

      fieldset.dataset.fieldsetIndex = String(index);

      radios.forEach((radio) => {
        radio.dataset.fieldsetIndex = String(index);
      });

      this.#radios.push(radios);

      const checked = radios.findIndex((radio) => radio.checked);
      this.#checkedIndices.push(checked < 0 ? [] : [checked]);
    });
  }

  #resizeObserver = new ResizeNotifier(() =>
    this.updateVariantPickerCss()
  );

  connectedCallback() {
    super.connectedCallback();

    this.#cielaChosenSize = '';
    this.#cielaResolvedVariant = '';
    const initialSizeState = this.#cielaRestoreUrlSize();
    this.#cielaRestoreSize();
    this.#cielaIndexFieldsets();

    this.addEventListener('change', this.#cielaChangeHandler);
    this.addEventListener('click', this.#cielaClickHandler);

    this.#cielaSetState(initialSizeState);
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver.disconnect();

    this.removeEventListener('change', this.#cielaChangeHandler);
    this.removeEventListener('click', this.#cielaClickHandler);

    this.#abortController?.abort();
    this.#cielaRequestVersion++;
  }

  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement
        ? event.target.options[event.target.selectedIndex]
        : event.target;

    if (!selectedOption?.dataset.optionValueId) return;

    if (this.hasAttribute('data-ciela-ring-size')) {
      if (event.target.closest('[data-ciela-size-option]')) {
        this.#cielaChosenSize = selectedOption.dataset.optionValueId;
      }

      this.#cielaResolvedVariant = '';

      this.#cielaSetState(
        this.#cielaChosenSize ? 'pending' : 'required'
      );
    }

    this.updateSelectedOption(event.target);

    this.dispatchEvent(
      new VariantSelectedEvent({
        id: selectedOption.dataset.optionValueId ?? '',
      })
    );

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;

    const loadsNewProduct =
      isOnProductPage && !!newUrl && newUrl !== currentUrl;

    const isOnFeaturedProductSection = Boolean(
      this.closest('featured-product-information')
    );

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
        ? 'featured-product-information'
        : undefined;

    this.fetchUpdatedSection(
      this.buildRequestUrl(selectedOption),
      morphElementSelector
    );

    const url = new URL(window.location.href);
    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = this.refs.fieldsets || [];
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth:
        currentIndex !== undefined
          ? radios[currentIndex]?.parentElement?.offsetWidth
          : undefined,
      previousWidth:
        previousIndex !== undefined
          ? radios[previousIndex]?.parentElement?.offsetWidth
          : undefined,
    };
  }

  #applyFieldsetMeasurements({
    fieldset,
    currentWidth,
    previousWidth,
    currentIndex,
    previousIndex,
  }) {
    if (currentWidth) {
      fieldset.style.setProperty(
        '--pill-width-current',
        `${currentWidth}px`
      );
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty(
        '--pill-width-previous',
        `${previousWidth}px`
      );
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);

    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(
        `[data-option-value-id="${target}"]`
      );

      if (!targetElement) {
        throw new Error('Target element not found');
      }

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(
        target.dataset.fieldsetIndex || ''
      );

      const inputIndex = Number.parseInt(
        target.dataset.inputIndex || ''
      );

      if (
        !Number.isNaN(fieldsetIndex) &&
        !Number.isNaN(inputIndex)
      ) {
        const fieldsets = this.refs.fieldsets || [];
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          const [currentIndex, previousIndex] = checkedIndices;

          if (
            currentIndex !== undefined &&
            radios[currentIndex]
          ) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }

          if (
            previousIndex !== undefined &&
            radios[previousIndex]
          ) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          const newCurrentIndex = checkedIndices[0];
          const newPreviousIndex = checkedIndices[1];

          if (
            newCurrentIndex !== undefined &&
            radios[newCurrentIndex]
          ) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (
            newPreviousIndex !== undefined &&
            radios[newPreviousIndex]
          ) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }

      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;

      const newSelectedOption = Array.from(target.options).find(
        (option) => option.value === newValue
      );

      if (!newSelectedOption) {
        throw new Error('Option not found');
      }

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = []
  ) {
    let productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;

    this.#pendingRequestUrl = productUrl;

    const params = [];
    const viewParamValue = getViewParameterValue();

    if (viewParamValue) {
      params.push(`view=${viewParamValue}`);
    }

    if (this.selectedOptionsValues.length && !source) {
      params.push(
        `option_values=${this.selectedOptionsValues.join(',')}`
      );
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(
          `option_values=${sourceSelectedOptionsValues.join(',')}`
        );
      } else {
        params.push(
          `option_values=${selectedOption.dataset.optionValueId}`
        );
      }
    }

    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component':
        'section-rendering-product-card',
      'featured-product-information':
        this.closest('featured-product-information')?.id,
    };

    const closestSectionId = Object.keys(SECTION_ID_MAP).find(
      (sectionId) => this.closest(sectionId)
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }

      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  fetchUpdatedSection(requestUrl, morphElementSelector) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const requestVersion = ++this.#cielaRequestVersion;

    fetch(requestUrl, {
      signal: this.#abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Variant request failed: ${response.status}`
          );
        }

        return response.text();
      })
      .then((responseText) => {
        if (requestVersion !== this.#cielaRequestVersion) return;

        this.#pendingRequestUrl = undefined;

        const html = new DOMParser().parseFromString(
          responseText,
          'text/html'
        );

        html
          .querySelector('overflow-list[defer]')
          ?.removeAttribute('defer');

        const textContent = html.querySelector(
          'variant-picker script[type="application/json"]'
        )?.textContent;

        if (!textContent) {
          throw new Error(
            'Variant response is missing product data'
          );
        }

        const variant = JSON.parse(textContent);
        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;

          const wasSwatchesExpanded =
            overflowList instanceof OverflowList &&
            overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;

            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(
              variant,
              this.selectedOptionId,
              {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              }
            )
          );
        }

        if (this.hasAttribute('data-ciela-ring-size')) {
          this.#cielaRestoreSize();
          this.#cielaIndexFieldsets();

          const size = this.querySelector(
            '[data-ciela-size-option] input:checked, [data-ciela-size-option] option[selected][data-option-value-id]'
          );

          const position = Number(this.dataset.cielaRingSize) - 1;

          const sizeMatches =
            size && variant?.options?.[position] === size.value;

          this.#cielaResolvedVariant =
            sizeMatches && variant.available
              ? String(variant.id)
              : '';

          this.#cielaSetState(
            !this.#cielaChosenSize
              ? 'required'
              : !variant
                ? 'unavailable'
                : !sizeMatches
                  ? 'error'
                  : variant.available
                    ? 'ready'
                    : 'unavailable'
          );
        }
      })
      .catch((error) => {
        if (requestVersion !== this.#cielaRequestVersion) return;

        this.#pendingRequestUrl = undefined;
        this.#cielaResolvedVariant = '';

        if (error.name !== 'AbortError') {
          this.#cielaSetState('error');
        }

        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  updateVariantPicker(newHtml) {
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(
      this.tagName.toLowerCase()
    );

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId =
        newVariantPickerSource.dataset.productId;

      const newProductUrl =
        newVariantPickerSource.dataset.productUrl;

      if (
        newProductId &&
        newProductUrl &&
        this.dataset.productId !== newProductId
      ) {
        newProduct = {
          id: newProductId,
          url: newProductUrl,
        };

        this.#cielaChosenSize = '';
        this.#cielaResolvedVariant = '';
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;

        const key = node.dataset.key;
        return key;
      },
    });

    this.#cielaRestoreSize();
    this.#cielaIndexFieldsets();
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = this.refs.fieldsets || [];

    const measurements = fieldsets
      .map((_, index) => this.#getFieldsetMeasurements(index))
      .filter((m) => m !== null);

    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(
        `No new element source found for ${elementSelector}`
      );
    }

    morph(element, newElement);
  }

  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  get selectedOption() {
    const selectedOption = this.querySelector(
      'select option[selected][data-option-value-id], fieldset input:checked[data-option-value-id]'
    );

    if (
      !(
        selectedOption instanceof HTMLInputElement ||
        selectedOption instanceof HTMLOptionElement
      )
    ) {
      return undefined;
    }

    return selectedOption;
  }

  get selectedOptionId() {
    const { selectedOption } = this;

    if (!selectedOption) return undefined;

    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  get selectedOptionsValues() {
    if (this.hasAttribute('data-ciela-ring-size')) {
      return Array.from(
        this.querySelectorAll('[data-ciela-option-position]')
      )
        .map((group) => {
          const selected = group.querySelector(
            'input:checked, option[selected][data-option-value-id]'
          );

          return (
            selected?.dataset.optionValueId ||
            group.dataset.cielaDefaultValueId
          );
        })
        .filter(Boolean);
    }

    const selectedOptions = Array.from(
      this.querySelectorAll(
        'select option[selected][data-option-value-id], fieldset input:checked[data-option-value-id]'
      )
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) {
        throw new Error('No option value ID found');
      }

      return optionValueId;
    });
  }
}

// Keep Shopify's select as the source of truth for variant updates.
class CielaVariantSelect extends HTMLElement {
  #observer = new MutationObserver(() => this.#sync());
  #active = -1;
  #search = '';
  #searchTimer;
  #focused = false;

  #pointerdown = (event) => {
    if (event.target.closest?.('[data-ciela-select-option]')) event.preventDefault();
  };

  #focus = (event) => {
    this.#focused = this.contains(event.target);
  };

  #click = (event) => {
    const item = event.target.closest?.('[data-ciela-select-option]');
    if (item && this.contains(item)) {
      event.preventDefault();
      this.#choose(item.dataset.value);
      return;
    }
    if (this.#trigger()?.contains(event.target)) {
      event.preventDefault();
      if (this.#isOpen()) this.#close();
      else this.#open();
    }
  };

  #outside = (event) => {
    if (!this.contains(event.target)) {
      this.#focused = false;
      this.#close();
    }
  };

  #blur = (event) => {
    if (!this.contains(event.relatedTarget)) this.#close();
  };

  #change = (event) => {
    if (event.target === this.querySelector('select')) this.#sync();
  };

  #keydown = (event) => {
    if (event.target !== this.#trigger() || event.ctrlKey || event.metaKey) return;
    const items = this.#items().filter((item) => item.getAttribute('aria-disabled') !== 'true');
    if (!items.length) return;
    const index = items.findIndex((item) => item === this.#items()[this.#active]);
    if (event.key === 'Tab') {
      this.#close();
      return;
    }
    if (event.key === 'Escape' || (event.altKey && event.key === 'ArrowUp')) {
      if (this.#isOpen()) event.preventDefault();
      this.#close();
      return;
    }
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      if (!this.#isOpen()) this.#open();
      else if (event.key === 'ArrowDown') this.#activate(items[Math.min(index + 1, items.length - 1)]);
      else if (event.key === 'ArrowUp') this.#activate(items[Math.max(index - 1, 0)]);
      if (event.key === 'Home') this.#activate(items[0]);
      if (event.key === 'End') this.#activate(items.at(-1));
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!this.#isOpen()) this.#open();
      else this.#choose(this.#items()[this.#active]?.dataset.value);
      return;
    }
    if (event.key.length === 1 && !event.altKey) {
      event.preventDefault();
      if (!this.#isOpen()) this.#open();
      clearTimeout(this.#searchTimer);
      this.#search += event.key.toLocaleLowerCase();
      const match = items.find((item) => item.textContent.trim().toLocaleLowerCase().startsWith(this.#search));
      if (match) this.#activate(match);
      this.#searchTimer = setTimeout(() => { this.#search = ''; }, 600);
    }
  };

  connectedCallback() {
    this.addEventListener('click', this.#click);
    this.addEventListener('pointerdown', this.#pointerdown);
    this.addEventListener('keydown', this.#keydown);
    this.addEventListener('focusout', this.#blur);
    this.addEventListener('change', this.#change);
    document.addEventListener('pointerdown', this.#outside);
    document.addEventListener('focusin', this.#focus);
    this.#observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['selected', 'disabled', 'value', 'data-enhanced'],
    });
    this.#sync();
  }

  disconnectedCallback() {
    this.#observer.disconnect();
    this.removeEventListener('click', this.#click);
    this.removeEventListener('pointerdown', this.#pointerdown);
    this.removeEventListener('keydown', this.#keydown);
    this.removeEventListener('focusout', this.#blur);
    this.removeEventListener('change', this.#change);
    document.removeEventListener('pointerdown', this.#outside);
    document.removeEventListener('focusin', this.#focus);
    clearTimeout(this.#searchTimer);
  }

  #trigger() { return this.querySelector('[data-ciela-select-trigger]'); }
  #list() { return this.querySelector('[data-ciela-select-list]'); }
  #items() { return Array.from(this.querySelectorAll('[data-ciela-select-option]')); }
  #isOpen() { return this.#trigger()?.getAttribute('aria-expanded') === 'true'; }

  #sync() {
    const select = this.querySelector('select');
    const trigger = this.#trigger();
    const list = this.#list();
    const value = this.querySelector('[data-ciela-select-value]');
    if (!select || !trigger || !list || !value) return;
    if (!this.hasAttribute('data-enhanced')) this.setAttribute('data-enhanced', '');
    if (trigger.disabled !== select.disabled) trigger.disabled = select.disabled;
    const text = select.options[select.selectedIndex]?.textContent.trim().replace(/\s+/g, ' ') || '';
    if (value.textContent !== text) value.textContent = text;
    const label = this.closest('.variant-option')?.querySelector('label');
    if (label) label.htmlFor = trigger.id;
    for (const item of this.#items()) {
      const option = Array.from(select.options).find((option) => option.value === item.dataset.value);
      item.setAttribute('aria-selected', String(!!option?.selected));
      item.setAttribute('aria-disabled', String(!option || option.disabled));
    }
    if (list.hidden) this.#close();
    if (this.#focused && this.isConnected && document.activeElement === document.body) {
      trigger.focus({ preventScroll: true });
    }
  }

  #open() {
    const trigger = this.#trigger();
    const list = this.#list();
    if (!trigger || trigger.disabled || !list) return;
    this.#sync();
    list.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    this.setAttribute('data-open', '');
    const items = this.#items().filter((item) => item.getAttribute('aria-disabled') !== 'true');
    this.#activate(items.find((item) => item.getAttribute('aria-selected') === 'true') || items[0]);
  }

  #activate(item) {
    if (!item) return;
    const items = this.#items();
    this.#active = items.indexOf(item);
    for (const option of items) option.classList.toggle('is-active', option === item);
    this.#trigger()?.setAttribute('aria-activedescendant', item.id);
    const list = this.#list();
    if (item.offsetTop < list.scrollTop) list.scrollTop = item.offsetTop;
    else if (item.offsetTop + item.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = item.offsetTop + item.offsetHeight - list.clientHeight;
    }
  }

  #close() {
    const trigger = this.#trigger();
    trigger?.setAttribute('aria-expanded', 'false');
    trigger?.removeAttribute('aria-activedescendant');
    const list = this.#list();
    if (list) list.hidden = true;
    this.removeAttribute('data-open');
    for (const item of this.#items()) item.classList.remove('is-active');
    this.#active = -1;
  }

  #choose(value) {
    const select = this.querySelector('select');
    const option = Array.from(select?.options || []).find((option) => option.value === value);
    if (!option || option.disabled || select.disabled) return;
    this.#close();
    this.#trigger()?.focus({ preventScroll: true });
    if (select.value === option.value) return;
    select.value = option.value;
    this.#sync();
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('ciela-variant-select')) {
  customElements.define('ciela-variant-select', CielaVariantSelect);
}


if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}